/**
 * InfoSection - Section heading + <dl> grid wrapper for read-only
 * info displays.
 *
 * Atomic Design role: Molecule. Pairs a section heading with a
 * vertical stack of InfoRow children (or any other content). Mirrors
 * the labelled section cards used by the JobForm sections so the
 * detail and edit pages share visual structure.
 *
 * Pass InfoRow children inside the dl-style container; the molecule
 * handles spacing + heading. When you need a non-standard layout
 * (e.g. a 2-column grid of rows), pass a custom `bodyClassName`.
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface InfoSectionProps {
	/** Section heading rendered above the rows. */
	heading: string;
	/** InfoRow children, or any other content. */
	children: React.ReactNode;
	/** Optional class override for the rows container (default vertical 12px stack). */
	bodyClassName?: string;
	/** Optional class override for the section root. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const InfoSection: React.FC<InfoSectionProps> = ({
	heading,
	children,
	bodyClassName,
	className,
}) => {
	const rootClass = `info-section ${className ?? ''}`.trim();
	const bodyClass = bodyClassName ?? 'space-y-12px';

	return (
		<div className={rootClass} data-ref="molecule-info-section">
			<h3 className="info-section__heading text-18px font-heading font-semibold text-brand-heading mb-16px">{heading}</h3>
			<dl className={`info-section__body ${bodyClass}`}>
				{children}
			</dl>
		</div>
	);
};

export default InfoSection;
